import { useState, useEffect, useRef } from "react";
import { jwtFetch } from "./utils/apiClient";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { format, subDays, addDays } from "date-fns";
import { config, encodeBase64RequestBody, priceFormat, numberFormat, slugFormat, shortenUrl } from "./Components/Config";
import Menu from "./Components/Menu";
import Header from "./Components/Header";
import {
  applyCurrencyConversion,
  recalculateItemTotals,
  calculateQuotationSubtotal,
  calculateQuotationCostTotal,
  calculateQuotationTotals,
} from "./utils/quotationCalculations";

const QuotationAdd = () => {
  const { id_company, id_subcompany, id_opportunity } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const contractScheduleId = searchParams.get("contract_shedule") || searchParams.get("contract_schedule");
  const [ companyList, setCompanyList ] = useState(null);
  const [ subcompanyList, setSubcompanyList ] = useState(null);
  const [ productList, setProductList ] = useState(null);
  const [ domainList, setDomainList ] = useState([]);
  const [ opportunity, setOpportunity ] = useState(null);
  const [ categoryList, setCategoryList ] = useState([]);
  const [ companyId, setCompanyId ] = useState(null);
  const [ subcompanyId, setSubcompanyId ] = useState(null);
  const [ quotationCurrency, setQuotationCurrency ] = useState('IDR');
  const [ quotationExchangeRate, setQuotationExchangeRate ] = useState(18000);
  const [ quotationExchangeFloating, setQuotationExchangeFloating ] = useState(true);
  const [ quotationCreatedDate, setQuotationCreatedDate ] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [ quotationExpiredDate, setQuotationExpiredDate ] = useState(format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'));
  const [ quotationSubtotal, setQuotationSubtotal ] = useState(0);
  const [ quotationDiscountPrice, setQuotationDiscountPrice ] = useState(0);
  const [ quotationDiscountRate, setQuotationDiscountRate ] = useState(0);
  const [ quotationTotal, setQuotationTotal ] = useState(0);
  const [ quotationCostTotal, setQuotationCostTotal ] = useState(0);
  const [ quotationMarginTotal, setQuotationMarginTotal ] = useState(0);
  const [ quotationMarginRate, setQuotationMarginRate ] = useState(0);
  const [ quotationBillingLongPeriod, setQuotationBillingLongPeriod ] = useState(1);
  const [ quotationBillingPaymentTerm, setQuotationBillingPaymentTerm ] = useState('Full Payment');
  const [ quotationBillingPaymentPeriod, setQuotationBillingPaymentPeriod ] = useState('One Time');
  const [ quotationCompanyAddress, setQuotationCompanyAddress ] = useState('');
  // Grouped: these three are always read and submitted together as one
  // "legal terms" concern, even though each field is edited independently.
  const [ quotationLegalTerms, setQuotationLegalTerms ] = useState({
    subscription: '',
    payment: '',
    cancellation: '',
  });
  const [ quotationItemList, setQuotationItemList ] = useState([]);
  const [ quotationBundleList, setQuotationBundleList ] = useState([]);
  const [ quotationPaymentOption, setQuotationPaymentOption ] = useState(100);
  const [ quotationRemarks, setQuotationRemarks ] = useState('');
  const [ hasCompanyLicence, setHasCompanyLicence ] = useState(false);
  const [ activeLicenceDate, setActiveLicenceDate ] = useState(null);
  const [ activePaymentPeriod, setActivePaymentPeriod ] = useState(null);
  // The currently-selected template option per term type, before "Insert"
  // copies its value into quotationLegalTerms.
  const [ selectedTermTemplate, setSelectedTermTemplate ] = useState({
    subscription: null,
    payment: null,
    cancellation: null,
  });
  const [ paymentList, setPaymentList ] = useState([]);
  const [ attachmentList, setAttachmentList ] = useState([]);
  const [ isInvoicingOnly, setIsInvoicingOnly ] = useState(false);
  const [ isKamigawe, setIsKamigawe ] = useState(false);
  const [ isLoadingSubmit, setIsLoadingSubmit ] = useState(false);
  const [ sourceOpportunityId, setSourceOpportunityId ] = useState(id_opportunity || null);
  const [ schedulePrefill, setSchedulePrefill ] = useState(null);
  const [ sourceQuotationPrefill, setSourceQuotationPrefill ] = useState(null);
  const companyPrefillAppliedRef = useRef(false);
  const subcompanyPrefillAppliedRef = useRef(false);
  const itemPrefillAppliedRef = useRef(false);
  
  const navigate = useNavigate();
  const requestedCompanyId =
    id_company && id_company !== 'null'
      ? id_company
      : schedulePrefill?.company_id
        ? String(schedulePrefill.company_id)
        : null;
  const requestedSubcompanyId =
    id_subcompany && id_subcompany !== 'null'
      ? id_subcompany
      : schedulePrefill?.subcompany_id
        ? String(schedulePrefill.subcompany_id)
        : null;
  const requestedScheduleLongPeriod = schedulePrefill?.schedule_long_period
    ? parseInt(schedulePrefill.schedule_long_period)
    : null;

  const productTaggingOptions = [
    { label: 'GWS SMB', value: 'GWS SMB' },
    { label: 'GWS CORP', value: 'GWS CORP' },
    { label: 'GWS ENTERPRISE', value: 'GWS ENTERPRISE' },
    { label: 'CHROME ENTERPRISE', value: 'CHROME ENTERPRISE' },
    { label: 'CHROME EDUCATION', value: 'CHROME EDUCATION' },
    { label: 'HARDWARE', value: 'HARDWARE' },
    { label: 'EDUCATION LICENSES', value: 'EDUCATION LICENSES' },
    { label: 'TRAINING', value: 'TRAINING' },
    { label: 'SERVICES', value: 'SERVICES' },
    { label: 'MICROSOFT', value: 'MICROSOFT' },
    { label: 'GOOGLE CLOUD', value: 'GOOGLE CLOUD' },
    { label: 'GEMINI ENTERPRISE', value: 'GEMINI ENTERPRISE' },
    { label: 'APIGEE', value: 'APIGEE' },
    { label: 'LUMAPPS', value: 'LUMAPPS' },
    { label: 'JUMPCLOUD', value: 'JUMPCLOUD' },
    { label: 'OTHER LICENSES', value: 'OTHER LICENSES' },
    { label: 'CUSTOM APPS', value: 'CUSTOM APPS' },
    { label: 'KAMIGAWE LICENSES', value: 'KAMIGAWE LICENSES' },
    { label: 'KAMIGAWE SERVICES', value: 'KAMIGAWE SERVICES' },
    { label: 'SAAS APPS (ALBAN)', value: 'SAAS APPS (ALBAN)' }
  ];
  const paymentTermOptions = [
    { label: 'Floating', value: `Please note that for any billing with a total value of less than IDR 5,000,000 (five million Rupiah), both invoice and corresponding tax invoice (faktur pajak) will be issued and delivered electronically (via softcopy) by default.\nPayments shall be calculated based on the JISDOR on the invoice issuance date.\nThe total invoiced amount must be received in full and prior to the due date. Any bank charges or fees incurred by the customer's bank shall be the sole responsibility of the customer.\nTaxes shall be applied at the prevailing rate on the invoice issuance date. If any additional tax applies, the customer must notify Polarisweb (PointStar) in writing at the time of payment.\nThe customer shall provide proof of payment and ensure the invoice number is clearly referenced in the transaction details.\nFailure to make timely payments may result in the suspension of services without prior notice, and the Company reserves the right to impose penalties or take further legal action to recover outstanding amounts.\nThese payment terms shall be governed by and construed in accordance with the applicable laws and regulations of the governing jurisdiction.\nAny disputes arising from these terms shall be resolved through good faith negotiations. If no resolution is reached, the matter shall be settled in accordance with the dispute resolution mechanism agreed upon by both parties.` },
    { label: 'Flat Rate', value: `Please note that for any billing with a total value of less than IDR 5,000,000 (five million Rupiah), both invoice and corresponding tax invoice (faktur pajak) will be issued and delivered electronically (via softcopy) by default.\nPayments shall be calculated based on the pre-agreed fixed exchange rate, as stated in the invoice or contractual agreement.\nThe total invoiced amount must be received in full and prior to the due date. Any bank charges or fees incurred by the customer's bank shall be the sole responsibility of the customer.\nTaxes shall be applied at the prevailing rate on the invoice issuance date. If any additional tax applies, the customer must notify the Company in writing at the time of payment.\nThe customer shall provide proof of payment and ensure the invoice number is clearly referenced in the transaction details.\nFailure to make timely payments may result in the suspension of services without prior notice. The Company reserves the right to impose penalties, charge interest on overdue amounts, or take further legal action as deemed necessary.\nThese payment terms shall be governed by and construed in accordance with the applicable laws and regulations of the governing jurisdiction.\nAny disputes arising from these terms shall be resolved through good faith negotiations. If no resolution is reached, the matter shall be settled in accordance with the dispute resolution mechanism agreed upon by both parties.` },
  ];
  const cancellationTermOptions = [
    { label: 'General', value: `Submit a formal written notice of termination via email or a signed letter from the highest-ranking officer of the Customer’s organization. Termination shall only take effect upon PointStar’s written acknowledgment and completion of all necessary discussions and settlement processes.\nFulfill all outstanding payment obligations for Subscriptions: If termination occurs prior to activation, no payment shall be required. If the subscription has been activated, the Customer shall remain liable for 100% of the total subscription fee.\nFulfill all outstanding payment obligations for Services: If termination occurs before service execution, the Customer shall pay 30% of the total agreed price. If services are in progress or have been completed, 100% of the total agreed price shall be due.\nReturn all Confidential Information belonging to PointStar that is in the Customer's possession or control.\nIndemnify and hold harmless PointStar against any claims, damages, losses, liabilities, costs, or expenses (including reasonable legal fees) arising from or in connection with the termination of this Agreement.` },
  ];

  useEffect(() => {
    getCompanyList();
    getProductList();
    getCategoryList();
    if (id_opportunity) {
      getOpportunity();
    }
    if (contractScheduleId) {
      getContractSchedulePrefill();
    }
  }, []);
  
  useEffect(() => {
    if (!companyList || !requestedCompanyId || companyPrefillAppliedRef.current) {
      return;
    }
    if (companyId && String(companyId.value) === String(requestedCompanyId)) {
      companyPrefillAppliedRef.current = true;
      return;
    }
    changeCompanyData(requestedCompanyId);
    companyPrefillAppliedRef.current = true;
  }, [companyList, requestedCompanyId, companyId]);

  useEffect(() => {
    setSubcompanyList(null);
    setSubcompanyId(null);
    setQuotationItemList([]);
    if (companyId) {
      const company = companyList.find(company => company.id_company == companyId.value);
      getCompanyDomains(company.company_domain);
      getSubcompanyList();
    }
  }, [companyId]);

  useEffect(() => {
    if (!subcompanyList || !requestedSubcompanyId || subcompanyPrefillAppliedRef.current) {
      return;
    }
    if (subcompanyId && String(subcompanyId.value) === String(requestedSubcompanyId)) {
      subcompanyPrefillAppliedRef.current = true;
      return;
    }
    changeSubcompanyData(requestedSubcompanyId);
    subcompanyPrefillAppliedRef.current = true;
  }, [subcompanyList, requestedSubcompanyId, subcompanyId]);

  useEffect(() => {
    if (subcompanyId) {
      const subcompany = subcompanyList.find(company => company.id_subcompany === subcompanyId.value);
      setQuotationItemList([]);
      setQuotationCompanyAddress([subcompany.company_billing_address]);
    }
  }, [subcompanyId]);

  useEffect(() => {
    if (schedulePrefill?.schedule_start_date) {
      setQuotationCreatedDate(format(new Date(schedulePrefill.schedule_start_date), 'yyyy-MM-dd'));
    }
  }, [schedulePrefill]);

  useEffect(() => {
    if (quotationCreatedDate) {
      setQuotationExpiredDate(format(addDays(new Date(quotationCreatedDate), 7), 'yyyy-MM-dd'));
    }
  }, [quotationCreatedDate]);

  useEffect(() => {
    if (
      !sourceQuotationPrefill ||
      !subcompanyId ||
      !productList ||
      domainList.length === 0 ||
      itemPrefillAppliedRef.current
    ) {
      return;
    }

    const sourceItems = (sourceQuotationPrefill.quotation_item_list || []).filter((item) => item.product_id);
    if (sourceItems.length === 0) {
      itemPrefillAppliedRef.current = true;
      return;
    }

    setQuotationCurrency(sourceQuotationPrefill.quotation_currency || 'IDR');
    setQuotationExchangeRate(sourceQuotationPrefill.quotation_exchange_rate || 16000);
    setQuotationExchangeFloating(sourceQuotationPrefill.quotation_exchange_floating === 'Floating');
    setQuotationDiscountPrice(parseFloat(sourceQuotationPrefill.quotation_discount_price || 0));
    setQuotationDiscountRate(parseFloat(sourceQuotationPrefill.quotation_discount_rate || 0));
    setQuotationLegalTerms({
      subscription: sourceQuotationPrefill.subscription_terms || '',
      payment: sourceQuotationPrefill.payment_terms || '',
      cancellation: sourceQuotationPrefill.cancellation_terms || '',
    });
    if (sourceItems[0]?.billing_long_period) {
      setQuotationBillingLongPeriod(sourceItems[0].billing_long_period);
    }
    if (sourceItems[0]?.billing_payment_term) {
      setQuotationBillingPaymentTerm(sourceItems[0].billing_payment_term);
    }
    if (sourceItems[0]?.billing_payment_period) {
      setQuotationBillingPaymentPeriod(sourceItems[0].billing_payment_period);
    }

    const mappedItems = sourceItems.map((item) => {
      const matchedProduct = productList.find((product) => parseInt(product.id_product) === parseInt(item.product_id));
      const productOption = matchedProduct
        ? { value: matchedProduct.id_product, label: matchedProduct.product_name }
        : { value: item.product_id, label: item.product_display_name || item.product_name || '' };

      return {
        product: productOption,
        product_id: item.product_id,
        product_name: matchedProduct?.product_name || item.product_name || '',
        product_display_name: item.product_display_name || '',
        product_display_description: item.product_display_description || '',
        product_type: item.product_type || matchedProduct?.product_type || '',
        product_category: matchedProduct?.product_category_id || '',
        product_domain: item.product_domain || domainList[0].domain,
        product_domain_new: '',
        product_currency_original: item.product_currency_original || 'IDR',
        product_currency: item.product_currency || sourceQuotationPrefill.quotation_currency || 'IDR',
        product_cost_original: parseFloat(item.product_cost_original || 0),
        product_cost: parseFloat(item.product_cost || 0),
        product_price_original: parseFloat(item.product_price_original || 0),
        product_price: parseFloat(item.product_price || 0),
        product_qty: parseFloat(item.product_qty || 1),
        product_subtotal: parseFloat(item.product_subtotal || 0),
        product_discount_price: parseFloat(item.product_discount_price || 0),
        product_discount_rate: parseFloat(item.product_discount_rate || 0),
        product_total: parseFloat(item.product_total || 0),
        product_margin_price: parseFloat(item.product_margin_price || 0),
        product_margin_rate: parseFloat(item.product_margin_rate || 0),
        product_tagging: item.product_tagging || '',
        service_type: item.service_type || 'New Order',
        is_upgrade: item.is_upgrade === 1 || item.is_upgrade === true,
        contract_long_period: requestedScheduleLongPeriod || parseInt(item.contract_long_period || 1),
        contract_duration_period: item.contract_duration_period || 'Month',
      };
    });

    setQuotationItemList(mappedItems);
    itemPrefillAppliedRef.current = true;
  }, [sourceQuotationPrefill, subcompanyId, productList, domainList, requestedScheduleLongPeriod]);

  useEffect(() => {
    const subtotal = calculateQuotationSubtotal(quotationItemList);
    const totalCost = calculateQuotationCostTotal(quotationItemList);
    setQuotationSubtotal(subtotal);
    setQuotationCostTotal(totalCost);
    if (quotationItemList.length === 0) {
      addQuotationItem();
    }
  }, [quotationItemList]);

  useEffect(() => {
    for (let i in quotationItemList) {
      quotationItemList[i].product_currency = quotationCurrency;
      const { product_cost, product_price } = applyCurrencyConversion(quotationItemList[i], quotationCurrency, quotationExchangeRate);
      quotationItemList[i].product_cost = product_cost;
      quotationItemList[i].product_price = product_price;
      Object.assign(quotationItemList[i], recalculateItemTotals(quotationItemList[i]));
    }
    setQuotationItemList([...quotationItemList]);
  }, [quotationCurrency, quotationExchangeRate]);

  useEffect(() => {
    const totalCost = calculateQuotationCostTotal(quotationItemList);
    const { quotationTotal, quotationMarginTotal, quotationMarginRate } = calculateQuotationTotals(
      quotationItemList,
      quotationSubtotal,
      quotationDiscountPrice
    );
    setQuotationTotal(quotationTotal);
    setQuotationCostTotal(totalCost);
    setQuotationMarginTotal(quotationMarginTotal);
    setQuotationMarginRate(quotationMarginRate);
  }, [quotationSubtotal, quotationDiscountPrice, quotationCostTotal]);

  useEffect(() => {
    if (quotationBillingPaymentTerm === 'Full Payment') {
      setQuotationBillingPaymentPeriod('One Time');
    } else {
      if (!hasCompanyLicence) {
        setQuotationBillingPaymentPeriod('Monthly');
      } else {
        setQuotationBillingPaymentPeriod(activePaymentPeriod);
      }
    }
  }, [quotationBillingPaymentTerm]);

  useEffect(() => {
    if (quotationBillingPaymentPeriod === 'Custom') {
      addPayment();
    } else {
      setPaymentList([]);
      switch (quotationBillingPaymentPeriod) {
        case 'Monthly': setQuotationBillingLongPeriod(1); break;
        case 'Bimonthly': setQuotationBillingLongPeriod(2); break;
        case 'Quarterly': setQuotationBillingLongPeriod(3); break;
        case 'Trimesterly': setQuotationBillingLongPeriod(4); break;
        case 'Semesterly': setQuotationBillingLongPeriod(6); break;
        case 'Annually': setQuotationBillingLongPeriod(12); break;
        default: setQuotationBillingLongPeriod(1); break;
      }
    }
  }, [quotationBillingPaymentPeriod]);

  useEffect(() => {
    let totalPaymentRate = paymentList.reduce((sum, payment) => sum + parseFloat(payment.payment_rate), 0);
    const updatedPaymentList = paymentList.map((payment, index) => {
      const error_rate = parseFloat((totalPaymentRate).toFixed(0)) !== 100 || payment.payment_rate === 0;
      const error_date = paymentList.some(
        (otherPayment, otherIndex) =>
          otherIndex !== index && otherPayment.start_term_period === payment.start_term_period
      );
      return { ...payment, error_rate, error_date };
    });

    const hasChanged = updatedPaymentList.some((payment, index) =>
      payment.error_rate !== paymentList[index].error_rate ||
      payment.error_date !== paymentList[index].error_date
    );
    if (hasChanged) {
      setPaymentList(updatedPaymentList);
    }
  }, [paymentList]);

  const getOpportunity = async () => {
    try {
      const requestOpportunity = {
        id_opportunity: id_opportunity,
      };
      const fetchOpportunity = await jwtFetch(config.ENDPOINT + 'get/opportunity', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestOpportunity)
      });
      const responseOpportunity = await fetchOpportunity.json();
      if (responseOpportunity.status === 200) {
        setOpportunity(responseOpportunity.data[0]);
        setSourceOpportunityId(responseOpportunity.data[0].id_opportunity);
      } else {
        alert(responseOpportunity.message);
      }
    } catch (error) {
      console.error('Error in getOpportunity:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const getContractSchedulePrefill = async () => {
    try {
      const fetchContractSchedule = await jwtFetch(config.ENDPOINT + 'get/contract_schedule', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_contract_schedule: contractScheduleId
        })
      });
      const responseContractSchedule = await fetchContractSchedule.json();
      if (responseContractSchedule.status !== 200 || responseContractSchedule.data.length === 0) {
        return;
      }

      const scheduleData = responseContractSchedule.data[0];
      if (!scheduleData.contract_id) {
        return;
      }

      const fetchContract = await jwtFetch(config.ENDPOINT + 'get/contract', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_contract: scheduleData.contract_id
        })
      });
      const responseContract = await fetchContract.json();
      if (responseContract.status !== 200 || responseContract.data.length === 0) {
        return;
      }

      const contractData = responseContract.data[0];
      setSchedulePrefill({
        company_id: contractData.company_id,
        subcompany_id: contractData.subcompany_id,
        schedule_start_date: scheduleData.schedule_start_date,
        schedule_long_period: scheduleData.schedule_long_period
      });

      if (contractData.quotation_id) {
        const fetchQuotation = await jwtFetch(config.ENDPOINT + 'get/quotation', {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id_quotation: contractData.quotation_id
          })
        });
        const responseQuotation = await fetchQuotation.json();
        if (responseQuotation.status === 200 && responseQuotation.data.length > 0) {
          const quotationData = responseQuotation.data[0];
          setSourceQuotationPrefill(quotationData);
          if (quotationData.opportunity_id) {
            setSourceOpportunityId(quotationData.opportunity_id);
          }
          if (quotationData.opportunity_id) {
            const fetchOpportunity = await jwtFetch(config.ENDPOINT + 'get/opportunity', {
              method: 'POST',
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                id_opportunity: quotationData.opportunity_id
              })
            });
            const responseOpportunity = await fetchOpportunity.json();
            if (responseOpportunity.status === 200 && responseOpportunity.data.length > 0) {
              setOpportunity(responseOpportunity.data[0]);
              setSourceOpportunityId(responseOpportunity.data[0].id_opportunity);
            }
          }
        }
      }
    } catch (error) {
      console.log('Error loading contract schedule prefill:', error);
    }
  }

  const getCompanyList = async () => {
    try {
      const requestCompanyList = {};
      const fetchCompanyList = await jwtFetch(config.ENDPOINT + 'get/company', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestCompanyList)
      });
      const responseCompanyList = await fetchCompanyList.json();
      if (responseCompanyList.status === 200) {
        setCompanyList(responseCompanyList.data.filter(company => company.company_status === 'Active').sort((a, b) => (a.company_name || '').localeCompare(b.company_name || '')));
      } else {
        alert(responseCompanyList.message);
      }
    } catch (error) {
      console.error('Error in getCompanyList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const changeCompanyData = (value) => {
    const company = companyList.find(company => company.id_company == value);
    if (!company) {
      return;
    }
    company.value = company.id_company;
    company.label = company.company_name;
    setCompanyId(company);
  }

  const getSubcompanyList = async () => {
    try {
      const requestSubcompanyList = {
        company_id: companyId.value
      }
      const fetchSubcompanyList = await jwtFetch(config.ENDPOINT + 'get/subcompany', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: encodeBase64RequestBody(requestSubcompanyList)
      });
      const responseSubcompanyList = await fetchSubcompanyList.json();
      if (responseSubcompanyList.status === 200) {
        setSubcompanyList(responseSubcompanyList.data.filter(subcompany => subcompany.company_status === 'Active').sort((a, b) => (a.company_name || '').localeCompare(b.company_name || '')));
      } else {
        alert(responseSubcompanyList.message);
      }
    } catch (error) {
      console.error('Error in getSubcompanyList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const changeSubcompanyData = (value) => {
    const subcompany = subcompanyList.find(subcompany => subcompany.id_subcompany == value);
    if (!subcompany) {
      return;
    }
    subcompany.value = subcompany.id_subcompany;
    subcompany.label = subcompany.company_name;
    setSubcompanyId(subcompany);
  }

  const getProductList = async () => {
    try {
      const requestProductList = {
        "product_status": "Active"
      }
      const fetchProductList = await jwtFetch(config.ENDPOINT + 'get/product', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestProductList)
      });
      const responseProductList = await fetchProductList.json();
      if (responseProductList.status === 200) {
        setProductList(responseProductList.data.filter(product => product.product_status === 'Active'));
      } else {
        alert(responseProductList.message);
      }
    } catch (error) {
      console.error('Error in getProductList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const getCategoryList = async () => {
    try {
      const requestCategoryList = {
        "product_category_status": "Active"
      }
      const fetchCategoryList = await jwtFetch(config.ENDPOINT + 'get/product_category', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestCategoryList)
      });
      const responseCategoryList = await fetchCategoryList.json();
      if (responseCategoryList.status === 200) {
        for (let i in responseCategoryList.data) {
          responseCategoryList.data[i].value = responseCategoryList.data[i].product_category_terms;
          responseCategoryList.data[i].label = responseCategoryList.data[i].product_category_name;
        }
        setCategoryList(responseCategoryList.data.filter(category => category.product_category_terms !== null).sort(category => (category.label || '').localeCompare(category.label || '')));
      } else {
        alert(responseCategoryList.message);
      }
    } catch (error) {
      console.error('Error in getCategoryList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const changeSubscriptionTerm = () => {
    if (selectedTermTemplate.subscription) {
      setQuotationLegalTerms(prev => ({ ...prev, subscription: selectedTermTemplate.subscription.value }));
    }
    setSelectedTermTemplate(prev => ({ ...prev, subscription: null }));
  }

  const changePaymentTerm = () => {
    if (selectedTermTemplate.payment) {
      setQuotationLegalTerms(prev => ({ ...prev, payment: selectedTermTemplate.payment.value }));
    }
    setSelectedTermTemplate(prev => ({ ...prev, payment: null }));
  }

  const changeCancellationTerm = () => {
    if (selectedTermTemplate.cancellation) {
      setQuotationLegalTerms(prev => ({ ...prev, cancellation: selectedTermTemplate.cancellation.value }));
    }
    setSelectedTermTemplate(prev => ({ ...prev, cancellation: null }));
  }

  const getCompanyLicence = async (productId, domain) => {
    try {
      const requestCompanyLicence = {
        "company_id": companyId.value,
        "product_id": productId,
        "licence_domain": domain,
      }
      const fetchCompanyLicence = await jwtFetch(config.ENDPOINT + 'get/clients_company_licence', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestCompanyLicence)
      });
      const responseCompanyLicence = await fetchCompanyLicence.json();
      if (responseCompanyLicence.status === 200) {
        if (responseCompanyLicence.data.length > 0) {
          return responseCompanyLicence.data[0];
        } else {
          return null;
        }
      } else {
        alert(responseCompanyLicence.message);
      }
    } catch (error) {
      console.error('Error in getCompanyLicence:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const getCompanyDomains = async (domain) => {
    try {
      const requestCompanyLicence = {
        "company_id": companyId.value,
      }
      const fetchCompanyLicence = await jwtFetch(config.ENDPOINT + 'get/clients_company_licence', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestCompanyLicence)
      });
      const responseCompanyLicence = await fetchCompanyLicence.json();
      if (responseCompanyLicence.status === 200) {
        const domains = [{
          domain: domain,
          product_id: null,
        }];
        if (responseCompanyLicence.data.length > 0) {
          for (let i in responseCompanyLicence.data) {
            const existingDomainIndex = domains.findIndex(item => item.domain === responseCompanyLicence.data[i].licence_domain && item.product_id === null);
            if (existingDomainIndex !== -1) {
              domains.splice(existingDomainIndex, 1);
            }
            domains.push({
              domain: responseCompanyLicence.data[i].licence_domain,
              product_id: responseCompanyLicence.data[i].product_id,
            });
          }
        }
        setDomainList(domains);
      } else {
        alert(responseCompanyLicence.message);
      }
    } catch (error) {
      console.error('Error in getCompanyDomains:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const getAdditionalLicence = async (requestAdditionalLicence) => {
    try {
      const fetchAdditionalLicence = await jwtFetch(config.ENDPOINT + 'simulation/additional_licence', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestAdditionalLicence)
      });
      const responseAdditionalLicence = await fetchAdditionalLicence.json();
      if (responseAdditionalLicence.status === 200) {
        if (responseAdditionalLicence.data.length > 0) {
          return responseAdditionalLicence.data;
        } else {
          return null;
        }
      } else {
        alert(responseAdditionalLicence.message);
      }
    } catch (error) {
      console.error('Error in getAdditionalLicence:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const addAttachments = () => {
    const attachment = {
      name: '',
      files: '',
      loading: false
    };
    setAttachmentList([...attachmentList, attachment]);
  }

  const changeAttachmentData = (column, value, index) => {
    const attachment = attachmentList[index];
    attachment[column] = value;
    if (column === 'files') {
      attachment.loading = false;
    }
    setAttachmentList([...attachmentList]);
  }

  const uploadAttachments = async (files, index) => {
    changeAttachmentData('loading', true, index);
    const formData = new FormData();
    files.forEach((attachment) => {
      formData.append('files', attachment);
    });
    try {      
      const responseUploadAttachments = await jwtFetch(config.ENDPOINT + 'upload/gdrive', {
        method: 'POST',
        body: formData,
      });
      if (!responseUploadAttachments.ok) {
        throw new Error(`Upload failed with status: ${responseUploadAttachments.status}`);
      }
      const resultUploadAttachments = await responseUploadAttachments.json();
      if (resultUploadAttachments.status === 200) {
        changeAttachmentData('files', resultUploadAttachments.data, index);
      } else {
        alert(`Upload error: ${resultUploadAttachments.message}`);
      }
    } catch (error) {
      alert(`Upload error: ${error.message || error}`);
    } finally {
      changeAttachmentData('loading', false, index);
    }
  }

  const removeAttachments = (index) => {
    attachmentList.splice(index, 1);
    setAttachmentList([...attachmentList]);
  }

  const addQuotationBundle = () => {
    const quotationBundle = {
      bundle_code: '',
      bundle_display_name: '',
      bundle_display_description: '',
      bundle_qty: 1,
      bundle_subtotal: 0,
      bundle_items: [],
    };
    setQuotationBundleList([...quotationBundleList, quotationBundle]);
  }

  const changeQuotationBundleData = (column, value, index) => {
    const quotationBundle = quotationBundleList[index];
    quotationBundle[column] = value;
    if (column === 'bundle_display_name') {
      quotationBundle.bundle_code = slugFormat(value);
    }
    setQuotationBundleList([...quotationBundleList]);
  }

  const changeQuotationBundleItem = (bundleItems, index) => {
    let subtotal = 0;
    for (let i in bundleItems) {
      subtotal += bundleItems[i].total;
    }
    let quotationBundles = quotationBundleList;
    quotationBundles[index].bundle_items = bundleItems;
    quotationBundles[index].bundle_subtotal = subtotal;
    setQuotationBundleList([...quotationBundles]);
  }

  const removeQuotationBundle = (index) => {
    quotationBundleList.splice(index, 1);
    setQuotationBundleList([...quotationBundleList]);
  }

  const addQuotationItem = () => {
    const quotationItem = {
      product: {},
      product_id: '',
      product_name: '',
      product_display_name: '',
      product_display_description: '',
      product_type: '',
      product_category: '',
      product_domain: domainList.length > 0 ? domainList[0].domain : '',
      product_domain_new: '',
      product_currency_original: '',
      product_currency: quotationCurrency,
      product_cost_original: 0,
      product_cost: 0,
      product_price_original: 0,
      product_price: 0,
      product_qty: 1,
      product_subtotal: 0,
      product_discount_price: 0,
      product_discount_rate: 0,
      product_total: 0,
      product_margin_price: 0,
      product_margin_rate: 0,
      product_tagging: '',
      service_type: 'New Order',
      is_upgrade: false,
      contract_long_period: requestedScheduleLongPeriod || 12,
      contract_duration_period: 'Month',
    };
    setQuotationItemList([...quotationItemList, quotationItem]);
  }

  const changeQuotationItemProduct = async (productId, domain, index) => {
    const product = productList.find(product => {
      return parseInt(product.id_product) === parseInt(productId.value);
    });
    const quotationItem = quotationItemList[index];
    let contract_duration = 0;
    const companyLicence = await getCompanyLicence(productId.value, domain);
    if (companyLicence) {
      setHasCompanyLicence(true);
      quotationItem.service_type = 'Additional';
      quotationItem.company_licence = companyLicence;
      quotationItem.product_active_licence_date = companyLicence.licence_start_period;
      product.product_price = companyLicence.licence_price_seat;
      setQuotationBillingPaymentTerm(companyLicence.billcycle_payment_term);
      setQuotationBillingPaymentPeriod(companyLicence.billcycle_payment_period);
      setActivePaymentPeriod(companyLicence.billcycle_payment_period);
      setActiveLicenceDate(companyLicence.licence_start_period);
      const additionalLicence = await getAdditionalLicence({
        "subscription": {
          "item_name": product.product_name,
          "sku_price": product.product_price,
          "num_user": companyLicence.total_licences,
          "product_discount_price": quotationItem.product_discount_price,
          "product_discount_prorate": 0,
          "billing_start_date": format(new Date(companyLicence.licence_start_period), 'dd MMM yyyy'),
          "installment_type": companyLicence.billcycle_payment_period,
          "total_contract_month": companyLicence.licence_long_period,
          "quotation_tax_rate": 12,
        },
        "additionalLicences": {
            "additional_licence_start_date": format(new Date(quotationCreatedDate), 'dd MMM yyyy'),
            "additional_licence_sku_price": product.product_price,
            "additional_licence_num_user": quotationItem.product_qty,
            "additional_licence_idr_usd_rate": quotationExchangeRate,
            "additional_licence_product_discount_price": quotationItem.product_discount_price,
            "additional_licence_product_discount_prorate": 0,
        },
      });
      if (additionalLicence) {
        for (let a in additionalLicence) {
          contract_duration += additionalLicence[a].number_of_months;
        }
        quotationItem.contract_long_period = contract_duration;
      }      
    } else {
      const anyAdditional = quotationItemList.some(item => item.service_type === 'Additional');
      if (!anyAdditional) {
        setQuotationBillingPaymentTerm('Full Payment');
        setQuotationBillingPaymentPeriod('One Time');
      }
      setActiveLicenceDate(null);
      setHasCompanyLicence(false);
      quotationItem.service_type = 'New Order';
      quotationItem.company_licence = null;
      quotationItem.product_active_licence_date = null;
      if (product.product_type === 'Subscription') {
        quotationItem.contract_long_period = 12;
      } else {
        quotationItem.contract_long_period = 1;
      }
    }

    quotationItem.product = productId;
    quotationItem.product_id = product.id_product;
    quotationItem.product_name = product.product_name;
    quotationItem.product_display_name = product.product_name;
    quotationItem.product_display_description = product.product_description;
    quotationItem.product_domain = domain;
    quotationItem.product_type = product.product_type;
    quotationItem.product_category = product.product_category_id;
    quotationItem.product_currency_original = product.product_currency;
    quotationItem.product_cost_original = product.product_cost;
    quotationItem.product_price_original = product.product_price;
    const { product_cost, product_price } = applyCurrencyConversion(quotationItem, quotationCurrency, quotationExchangeRate);
    quotationItem.product_cost = product_cost;
    quotationItem.product_price = product_price;
    Object.assign(quotationItem, recalculateItemTotals(quotationItem));
    setQuotationItemList([...quotationItemList]);
  }


  const changeQuotationItemData = (column, value, index) => {
    const quotationItem = quotationItemList[index];
    quotationItem[column] = value;
    if (column === 'product_cost_original') {
      quotationItem.product_cost = parseFloat((quotationItem.product_cost_original * quotationExchangeRate).toFixed(3));
    } else if (column === 'product_cost') {
      quotationItem.product_cost_original = parseFloat((quotationItem.product_cost / quotationExchangeRate).toFixed(3));
    } else if (column === 'product_price_original') {
      quotationItem.product_price = parseFloat((quotationItem.product_price_original * quotationExchangeRate).toFixed(3));
    } else if (column === 'product_price') {
      quotationItem.product_price_original = parseFloat((quotationItem.product_price / quotationExchangeRate).toFixed(3));
    } else if (column === 'product_discount_price') {
      quotationItem.product_discount_rate = parseFloat((quotationItem.product_discount_price / quotationItem.product_subtotal * 100).toFixed(3));
    } else if (column === 'product_discount_rate') {
      quotationItem.product_discount_price = parseFloat((quotationItem.product_subtotal * quotationItem.product_discount_rate / 100).toFixed(3));
    }
    Object.assign(quotationItem, recalculateItemTotals(quotationItem));
    setQuotationItemList([...quotationItemList]);
  }

  const removeQuotationItem = (index) => {
    quotationItemList.splice(index, 1);
    setQuotationItemList([...quotationItemList]);
  }

  const changeQuotationDiscount = (column, value) => {
    if (column === 'quotation_discount_rate') {
      setQuotationDiscountRate(value);
      setQuotationDiscountPrice(parseFloat((quotationSubtotal * value / 100).toFixed(3)));
    } else if (column === 'quotation_discount_price') {
      setQuotationDiscountRate(parseFloat((value / quotationSubtotal * 100).toFixed(3)));
      setQuotationDiscountPrice(value);
    }
  }

  const addPayment = () => {
    const payment = {
      number_of_months: 1,
      start_term_period: paymentList ? format(new Date(), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      end_term_period: null,
      payment_amount_exc_tax: 0,
      payment_amount_inc_tax: 0,
      payment_rate: 0,
      error_rate: true,
      error_date: true
    }
    setPaymentList([...paymentList, payment]);
  }

  const changePaymentData = (column, value, index) => {
    const payment = paymentList[index];
    payment[column] = value;
    if (column === 'payment_rate') {
      payment.payment_amount_exc_tax = parseFloat((quotationTotal * value / 100).toFixed(3));
    } else if (column === 'payment_amount_exc_tax') {
      payment.payment_rate = parseFloat((value / quotationTotal * 100).toFixed(3));
    }
    setPaymentList([...paymentList]);
  }

  const removePayment = (index) => {
    paymentList.splice(index, 1);
    setPaymentList([...paymentList]);
  }

  const changeQuotationPaymentOption = (index, value) => {
    let quotationItem = quotationItemList[index];
    quotationItem.contract_long_period = value * quotationBillingLongPeriod;
    quotationItem.product_price = quotationItem.product_price / (value * quotationBillingLongPeriod);
    setQuotationPaymentOption(value);
    setQuotationItemList([...quotationItemList]);
  }

  const submitQuotationAdd = async () => {
    try {
      setIsLoadingSubmit(true);

      let errorSubmit = null;
      if (paymentList.some(payment => payment.error_rate)) {
        errorSubmit = 'Total payment percentage must be 100%';
      }
      if (quotationMarginTotal <= 0) {
        errorSubmit = 'Total margin is less than or 0';
      }
      if (quotationLegalTerms.cancellation === '') {
        errorSubmit = 'Cancellation term is required';
      }
      if (quotationLegalTerms.payment === '') {
        errorSubmit = 'Payment term is required';
      }
      if (quotationItemList.some(item => item.product_type === 'Subscription')) {
        if (quotationLegalTerms.subscription === '') {
          errorSubmit = 'Subscription term is required';
        }
      }
      if (quotationItemList.some(item => !item.product_tagging || item.product_tagging === '')) {
        errorSubmit = 'Product tagging is required for all items';
      }
      if (!attachmentList.length || attachmentList.some(attachment => !attachment.name)) {
        errorSubmit = 'Please add at least one attachment';
      }
      if (errorSubmit) {
        alert(errorSubmit);
        setIsLoadingSubmit(false);
        return;
      }
      const crmUser = JSON.parse(localStorage.getItem('crmUser'));
      const quotationItems = [];
      const quotationItemLists = [];
      const quotationCustomPayment = [];

      for (let i in quotationBundleList) {
        const productProrate = parseFloat((quotationBundleList[i].bundle_subtotal / quotationSubtotal).toFixed(3));
        const discountProrate = parseFloat((quotationDiscountPrice * productProrate).toFixed(3));
        quotationItems.push({
          "bundle_code": quotationBundleList[i].bundle_code,
          "product_display_name": quotationBundleList[i].bundle_display_name,
          "product_display_description": quotationBundleList[i].bundle_display_description,
          "product_type": "Bundle",
          "product_domain": quotationItemList[0].product_domain ? quotationItemList[0].product_domain : quotationItemList[0].product_domain_new,
          "product_qty": 1,
          "product_currency_original": quotationCurrency,
          "product_currency": quotationCurrency,
          "product_cost_original": 0,
          "product_cost": 0,
          "product_price_original": parseFloat((quotationBundleList[i].bundle_subtotal / quotationItemList[0].contract_long_period).toFixed(3)),
          "product_price": parseFloat((quotationBundleList[i].bundle_subtotal / quotationItemList[0].contract_long_period).toFixed(3)),
          "product_subtotal": quotationBundleList[i].bundle_subtotal,
          "product_discount_price": 0,
          "product_discount_rate": 0,
          "product_discount_prorate": discountProrate,
          "product_margin_price": 0,
          "product_margin_rate": 0,
          "product_total": quotationBundleList[i].bundle_subtotal,
          "product_grand_total": quotationBundleList[i].bundle_subtotal,
          "product_active_licence_date": quotationItemList[0].product_active_licence_date ? format(quotationItemList[0].product_active_licence_date, 'yyyy-MM-dd') : null,
          "product_tagging": quotationItemList[0].product_tagging,
          "service_type": 'New Order',
          "is_upgrade": quotationItemList[0].is_upgrade,
          "contract_long_period": quotationItemList[0].contract_long_period,
          "contract_duration_period": 'Month',
          "billing_long_period": quotationBillingLongPeriod,
          "billing_duration_period": 'Month',
          "billing_payment_term": quotationBillingPaymentTerm,
          "billing_payment_period": quotationBillingPaymentPeriod,
        });
      }

      for (let i in quotationItemList) {
        const productProrate = parseFloat((quotationItemList[i].product_total / quotationSubtotal).toFixed(3));
        const discountProrate = parseFloat((quotationDiscountPrice * productProrate).toFixed(3));
        const grandTotal = quotationItemList[i].product_total - discountProrate;
        if (quotationItemList[i].product_id) {
          let bundleCode = null;
          for (let j in quotationBundleList) {
            if (quotationBundleList[j].bundle_items.find((product) => product.value === quotationItemList[i].product_id)) {
              bundleCode = quotationBundleList[j].bundle_code;
            }
          }
          if (!bundleCode) {
            quotationItems.push({
              "bundle_code": bundleCode,
              "product_id": quotationItemList[i].product_id,
              "product_display_name": quotationItemList[i].product_display_name,
              "product_display_description": quotationItemList[i].product_display_description,
              "product_type": quotationItemList[i].product_type,
              "product_domain": quotationItemList[0].product_domain && quotationItemList[0].product_domain !== 'new' ? quotationItemList[0].product_domain : quotationItemList[0].product_domain_new,
              "product_qty": quotationItemList[i].product_qty,
              "product_currency_original": quotationItemList[i].product_currency_original,
              "product_currency": quotationCurrency,
              "product_cost_original": quotationItemList[i].product_currency_original === quotationCurrency ? quotationItemList[i].product_cost : quotationItemList[i].product_cost_original,
              "product_cost": quotationItemList[i].product_cost,
              "product_price_original": quotationItemList[i].product_currency_original === quotationCurrency ? quotationItemList[i].product_price : quotationItemList[i].product_price_original,
              "product_price": quotationItemList[i].product_price,
              "product_subtotal": quotationItemList[i].product_subtotal,
              "product_discount_price": quotationItemList[i].product_discount_price,
              "product_discount_rate": quotationItemList[i].product_discount_rate,
              "product_discount_prorate": discountProrate,
              "product_margin_price": quotationItemList[i].product_margin_price,
              "product_margin_rate": quotationItemList[i].product_margin_rate,
              "product_total": quotationItemList[i].product_total,
              "product_grand_total": grandTotal,
              "product_active_licence_date": quotationItemList[i].product_active_licence_date ? format(quotationItemList[i].product_active_licence_date, 'yyyy-MM-dd') : null,
              "product_tagging": quotationItemList[i].product_tagging,
              "service_type": quotationItemList[i].service_type,
              "is_upgrade": quotationItemList[i].is_upgrade,
              "contract_long_period": quotationItemList[i].contract_long_period,
              "contract_duration_period": 'Month',
              "billing_long_period": quotationBillingLongPeriod,
              "billing_duration_period": 'Month',
              "billing_payment_term": quotationBillingPaymentTerm,
              "billing_payment_period": quotationBillingPaymentPeriod,
            });
          }
          quotationItemLists.push({
            "bundle_code": bundleCode,
            "product_id": quotationItemList[i].product_id,
            "product_display_name": quotationItemList[i].product_display_name,
            "product_display_description": quotationItemList[i].product_display_description,
            "product_type": quotationItemList[i].product_type,
            "product_domain": quotationItemList[0].product_domain && quotationItemList[0].product_domain !== 'new' ? quotationItemList[0].product_domain : quotationItemList[0].product_domain_new,
            "product_qty": quotationItemList[i].product_qty,
            "product_currency_original": quotationItemList[i].product_currency_original,
            "product_currency": quotationCurrency,
            "product_cost_original": quotationItemList[i].product_currency_original === quotationCurrency ? quotationItemList[i].product_cost : quotationItemList[i].product_cost_original,
            "product_cost": quotationItemList[i].product_cost,
            "product_price_original": quotationItemList[i].product_currency_original === quotationCurrency ? quotationItemList[i].product_price : quotationItemList[i].product_price_original,
            "product_price": quotationItemList[i].product_price,
            "product_subtotal": quotationItemList[i].product_subtotal,
            "product_discount_price": quotationItemList[i].product_discount_price,
            "product_discount_rate": quotationItemList[i].product_discount_rate,
            "product_discount_prorate": discountProrate,
            "product_margin_price": quotationItemList[i].product_margin_price,
            "product_margin_rate": quotationItemList[i].product_margin_rate,
            "product_total": quotationItemList[i].product_total,
            "product_grand_total": grandTotal,
            "product_active_licence_date": quotationItemList[i].product_active_licence_date ? format(quotationItemList[i].product_active_licence_date, 'yyyy-MM-dd') : null,
            "product_tagging": quotationItemList[i].product_tagging,
            "service_type": quotationItemList[i].service_type,
            "is_upgrade": quotationItemList[i].is_upgrade,
            "contract_long_period": quotationItemList[i].contract_long_period,
            "contract_duration_period": 'Month',
            "billing_long_period": quotationBillingLongPeriod,
            "billing_duration_period": 'Month',
            "billing_payment_term": quotationBillingPaymentTerm,
            "billing_payment_period": quotationBillingPaymentPeriod,
          });
        }
      }
      if (quotationBillingPaymentPeriod === 'Custom') {
        for (let i in paymentList) {
          quotationCustomPayment.push({
            "term_number": "Term " + (parseInt(i) + 1),
            "number_of_months": 1,
            "start_term_period": format(paymentList[i].start_term_period, 'yyyy-MM-dd 00:00:00'),
            "end_term_period": format(paymentList[i].start_term_period, 'yyyy-MM-dd 23:59:59'),
            "payment_amount_exc_tax": paymentList[i].payment_amount_exc_tax,
            "payment_amount_inc_tax": paymentList[i].payment_amount_inc_tax,
            "payment_rate": paymentList[i].payment_rate
          })
        }
      }
      const requestQuotationAdd = {
        "opportunity_id": sourceOpportunityId ? sourceOpportunityId : null,
        "contract_schedule_id": contractScheduleId ? parseInt(contractScheduleId) : null,
        "company_id": companyId.value,
        "subcompany_id": subcompanyId ? subcompanyId.value : null,
        "quotation_currency": quotationCurrency,
        "quotation_exchange_rate": quotationExchangeRate,
        "quotation_exchange_floating": quotationExchangeFloating ? 'Floating' : 'Flat Rate',
        "quotation_expired_date": quotationExpiredDate,
        "quotation_subtotal": quotationSubtotal,
        "quotation_discount_rate": quotationDiscountRate,
        "quotation_discount_price": quotationDiscountPrice,
        "quotation_cost": quotationCostTotal,
        "quotation_margin_rate": quotationMarginRate,
        "quotation_margin_price": quotationMarginTotal,
        "quotation_total": quotationTotal,
        "quotation_approval": "Draft",
        "quotation_status": "Draft",
        "subscription_terms": quotationLegalTerms.subscription,
        "payment_terms": quotationLegalTerms.payment,
        "cancellation_terms": quotationLegalTerms.cancellation,
        "company_address": quotationCompanyAddress,
        "created_on": quotationCreatedDate + " " + format(new Date(), 'HH:mm:ss'),
        "created_by": crmUser.user_keyid,
        "quotation_items": quotationItems,
        "quotation_item_list": quotationItemLists,
        "quotation_custom_payment": quotationCustomPayment,
        "quotation_remarks": quotationRemarks,
        "attachment_files": attachmentList.length > 0 ? JSON.stringify(attachmentList) : null,
        "is_invoicing_only": isInvoicingOnly,
        "is_kamigawe": isKamigawe,
      };

      const fetchQuotationAdd = await jwtFetch(config.ENDPOINT + 'create/quotation', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: encodeBase64RequestBody(requestQuotationAdd)
      });
      const responseQuotationAdd = await fetchQuotationAdd.json();
      if (responseQuotationAdd.status === 200) {
        window.location.href = '/quotation/detail/' + responseQuotationAdd.data.insertId;
      } else {
        alert(responseQuotationAdd.message);
        setIsLoadingSubmit(false);
      }
    } catch (error) {
      console.error('Error in submitQuotationAdd:', error);
      alert('An error occurred: ' + (error.message || error));
      setIsLoadingSubmit(false);
    }
  }

  return (
    <div id="quotation-add">
      <div className="container-fluid p-0">
        <div className="row g-0 mh-100vh">
          <div className="col-md-2 border-end">
            <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
              <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar"/></a>
            </div>
            <hr className="m-0"/>
            <Menu active="quotation"/>
          </div>
          <div className="col-md-10">
            <Header/>
            <hr className="m-0"/>
            <div className="main">
              <div className="p-4">
                
                <div className="mb-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="fs-24"><button className="btn btn-link" onClick={() => navigate(-1)}><i className="bi bi-arrow-left"/></button> Quotation Add</div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="row align-items-baseline form-group mb-2">
                        <div className="col-md-4">
                          <label>Company Group <span className="text-danger">*</span></label>
                        </div>
                        <div className="col-md-8">
                          <Select value={companyId} options={companyList && companyList.map(company => ({ value: company.id_company, label: company.company_name }))} onChange={(value) => setCompanyId(value)} placeholder="" required />
                        </div>
                      </div>
                      <div className="row align-items-baseline form-group mb-2">
                        <div className="col-md-4">
                          <label>Subsidiary <span className="text-danger">*</span></label>
                        </div>
                        <div className="col-md-8">
                          <Select value={subcompanyId} options={subcompanyList && subcompanyList.map(subcompany => ({ value: subcompany.id_subcompany, label: subcompany.company_name }))} onChange={(value) => setSubcompanyId(value)} placeholder="" isDisabled={!subcompanyList} required />
                        </div>
                      </div>
                      <div className="row align-items-baseline form-group mb-2">
                        <div className="col-md-4">
                          <label>Currency <span className="text-danger">*</span></label>
                        </div>
                        <div className="col-md-8">
                          <select className="form-select" value={quotationCurrency} onChange={(e) => setQuotationCurrency(e.target.value)} required>
                            {/* <option value="USD">USD</option> */}
                            <option value="IDR">IDR</option>
                            {/* <option value="SGD">SGD</option>
                            <option value="MYR">MYR</option> */}
                          </select>
                        </div>
                      </div>
                      <div className="row align-items-baseline form-group mb-2">
                        <div className="col-md-4">
                          <label>Exchange Rate <span className="text-danger">*</span></label>
                        </div>
                        <div className="col-md-4">
                          <div className="input-group mb-2">
                            <input className="form-control text-end" type="text" value={1} disabled required/>
                            <span className="input-group-text">USD</span>
                          </div>
                          <div className="d-flex gap-3">
                            <div className="form-check">
                              <input className="form-check-input" type="radio" name="exchange-options" id="option-floating" checked={quotationExchangeFloating === true} onChange={() => setQuotationExchangeFloating(true)}/>
                              <label className="form-check-label" htmlFor="option-floating">
                                Floating
                              </label>
                            </div>
                            <div className="form-check">
                              <input className="form-check-input" type="radio" name="exchange-options" id="option-flat" checked={quotationExchangeFloating === false} onChange={() => setQuotationExchangeFloating(false)}/>
                              <label className="form-check-label" htmlFor="option-flat">
                                Flat
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="input-group mb-1">
                            <input className="form-control text-end" type="text" value={priceFormat(quotationExchangeRate)} onChange={(e) => setQuotationExchangeRate(numberFormat(e.target.value))} required/>
                            <span className="input-group-text">{quotationCurrency}</span>
                          </div>
                        </div>
                      </div>
                      <div className="row align-items-baseline form-group mb-2">
                        <div className="col-md-4">
                          <label>Effective Date <span className="text-danger">*</span></label>
                        </div>
                        <div className="col-md-8">
                          <input className="form-control" type="date" value={quotationCreatedDate} onChange={(e) => setQuotationCreatedDate(e.target.value)} required/>
                        </div>
                      </div>
                      <div className="row align-items-baseline form-group mb-2">
                        <div className="col-md-4">
                          <label>Expiry Date <span className="text-danger">*</span></label>
                        </div>
                        <div className="col-md-8">
                          <input className="form-control" type="date" value={quotationExpiredDate} onChange={(e) => setQuotationExpiredDate(e.target.value)} required/>
                        </div>
                      </div>
                      <div className="row align-items-baseline form-group">
                        <div className="col-md-4">
                          <label>Is it for invoicing only?</label>
                        </div>
                        <div className="col-md-8">
                          <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" id="is-invoicing-only" onChange={() => setIsInvoicingOnly(!isInvoicingOnly)}/>
                            <label className="form-check-label" htmlFor="is-invoicing-only">{isInvoicingOnly ? 'Yes' : 'No'}</label>
                          </div>
                        </div>
                      </div>
                      <div className="row align-items-baseline form-group">
                        <div className="col-md-4">
                          <label>Is it for Kamigawe?</label>
                        </div>
                        <div className="col-md-8">
                          <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" id="is-kamigawe" onChange={() => setIsKamigawe(!isKamigawe)}/>
                            <label className="form-check-label" htmlFor="is-kamigawe">{isKamigawe ? 'Yes' : 'No'}</label>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      {opportunity && (
                        <div className="card mb-2">
                          <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <p className="fs-14 mb-0"><strong>Source</strong></p>
                            </div>
                            <div className="p-2">
                              {opportunity.lead_code && (
                                <div className="row mb-2">
                                  <div className="col-md-4 text-secondary">Lead</div>
                                  <div className="col-md-8">
                                    {opportunity.lead_code}
                                    <a className="text-decoration-none" href={`/lead/detail/${opportunity.lead_id}`} target="_blank" rel="noopener noreferrer"><i className="bi bi-link-45deg ms-2"/></a>
                                  </div>
                                </div>
                              )}
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">Opportunity</div>
                                <div className="col-md-8">
                                  {opportunity.opportunity_code}
                                    <a className="text-decoration-none" href={`/opportunity/detail/${opportunity.id_opportunity}`} target="_blank" rel="noopener noreferrer"><i className="bi bi-link-45deg ms-2"/></a>
                                </div>
                              </div>
                              {opportunity.contact_name && (
                                <div className="row mb-2">
                                  <div className="col-md-4 text-secondary">Contact Person</div>
                                  <div className="col-md-8">
                                    {opportunity.contact_name}
                                    <a className="text-decoration-none" href={`/clients/contact/${opportunity.contact_id}`} target="_blank" rel="noopener noreferrer"><i className="bi bi-link-45deg ms-2"/></a>
                                  </div>
                                </div>
                              )}
                              {!opportunity.company_name && opportunity.website_form && (
                                <div className="row mb-2">
                                  <div className="col-md-4 text-secondary">Company Name</div>
                                  <div className="col-md-8">{JSON.parse(opportunity.website_form).company_name}</div>
                                </div>
                              )}
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">Deal Type</div>
                                <div className="col-md-8">{opportunity.opportunity_deal_type}</div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">Product Line</div>
                                <div className="col-md-8">{opportunity.opportunity_product_line}</div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">Product Detail</div>
                                <div className="col-md-8">{opportunity.opportunity_product_detail}</div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">Months</div>
                                <div className="col-md-8">{opportunity.opportunity_product_months > 1 ? opportunity.opportunity_product_months + " months" : "1 month"}</div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">Estimated Revenue</div>
                                <div className="col-md-8">{priceFormat(opportunity.opportunity_expected_revenue, 'IDR')}</div>
                              </div>
                              <div className="row mb-2">
                                <div className="col-md-4 text-secondary">Estimated Profit</div>
                                <div className="col-md-8">{priceFormat(opportunity.opportunity_expected_profit, 'IDR')}</div>
                              </div>
                              <div className="row">
                                <div className="col-md-4 text-secondary">Remarks</div>
                                <div className="col-md-8">{opportunity.opportunity_remarks}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {subcompanyId && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">SKU List</div>
                    </div>
                    {quotationItemList.map((item, i) =>
                      <div className="card mb-3" key={"item-" + i}>
                        <div className="card-body">
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="fs-18"><strong>SKU #{i + 1}</strong></div>
                            <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => removeQuotationItem(i)}><i className="bi bi-trash me-2"/> Remove</button>
                          </div>
                          <div className="row">
                            <div className="col-md-6">
                              <div className="row align-items-baseline form-group mb-2">
                                <div className="col-md-4">
                                  <label>Internal Product Name <span className="text-danger">*</span></label>
                                </div>
                                <div className="col-md-8">
                                  <Select value={item.product} options={productList && [...productList].sort((a, b) => (a.product_name || '').localeCompare(b.product_name || '')).map(product => ({ value: product.id_product, label: product.product_name }))} onChange={(value) => changeQuotationItemProduct(value, item.product_domain, i)} isDisabled={companyId === null} required />
                                </div>
                              </div>
                              {item.product_id && (
                                <div>
                                  <div className="row align-items-baseline form-group mb-2">
                                    <div className="col-md-4">
                                      <label>Display Name in QT<span className="text-danger">*</span></label>
                                    </div>
                                    <div className="col-md-8">
                                      <input className="form-control" type="text" value={item.product_display_name} onChange={(e) => changeQuotationItemData('product_display_name', e.target.value, i)} required/>
                                    </div>
                                  </div>
                                  <div className="row align-items-baseline form-group mb-2">
                                    <div className="col-md-4">
                                      <label>Display Description <span className="text-danger">*</span></label>
                                    </div>
                                    <div className="col-md-8">
                                      <textarea className="form-control" rows="4" value={item.product_display_description} onChange={(e) => changeQuotationItemData('product_display_description', e.target.value, i)} required/>
                                    </div>
                                  </div>
                                  {(quotationItemList.some(item => item.service_type === 'Additional') && item.service_type !== 'Additional') ? (
                                    <>
                                      <div className="row align-items-baseline form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Payment Option <span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <select className="form-select" value={quotationPaymentOption} onChange={(e) => changeQuotationPaymentOption(i, e.target.value)} required>
                                            <option value=""></option>
                                            <option value="1">1 x 100%</option>
                                            {/* <option value="2" disabled>2 x 50%</option> */}
                                          </select>
                                        </div>
                                      </div>
                                      <div className="row align-items-center form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Contract Period/Multipliers <span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.contract_long_period)} onChange={(e) => changeQuotationItemData('contract_long_period', numberFormat(e.target.value), i)} disabled={item.service_type === 'Additional'} required/>
                                            <span className="input-group-text mw-85">Month(s)</span>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="row align-items-baseline form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Quantity <span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.product_qty)} onChange={(e) => changeQuotationItemData('product_qty', numberFormat(e.target.value), i)} required/>
                                            <span className="input-group-text mw-85">Unit(s)</span>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="row align-items-baseline form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Tagging <span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <select className="form-select" value={item.product_tagging} onChange={(e) => changeQuotationItemData('product_tagging', e.target.value, i)} required>
                                            <option value=""></option>
                                            {productTaggingOptions.map((item, index) => (
                                              <option key={index} value={item.value}>{item.label}</option>
                                            ))}
                                          </select>
                                        </div>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="row align-items-baseline form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Domain for Provisioning<span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <select className="form-select" value={item.product_domain} onChange={(e) => changeQuotationItemProduct(item.product, e.target.value, i)} required>
                                            <option value={domainList[0].domain}>{domainList[0].domain}</option>
                                            {domainList.filter(domain => domain.product_id === item.product_id && domain.domain !== domainList[0].domain).sort((a, b) => (a.domain || '').localeCompare(b.domain || '')).map((domain) =>
                                              <option key={'domain-' + domain.domain} value={domain.domain}>{domain.domain}</option>
                                            )}
                                            <option value="new">New domain</option>
                                          </select>
                                          {item.product_domain === 'new' && (
                                            <input className="form-control mt-2" type="text" value={item.product_domain_new} placeholder="Type new domain..." onChange={(e) => changeQuotationItemData('product_domain_new', e.target.value, i)} required/>
                                          )}
                                        </div>
                                      </div>
                                      <div className="row align-items-baseline form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Order Type <span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <select className="form-select" value={item.service_type} onChange={(e) => changeQuotationItemData('service_type', e.target.value, i)} disabled={!(item.company_licence)} required>
                                            {!(item.company_licence) && (<option value="New Order">New Order</option>)}
                                            {item.company_licence && item.company_licence.licence_status === 'Active' && (<option value="Additional">Additional</option>)}
                                            {item.company_licence && item.company_licence.licence_status === 'Active' && (<option value="Renewal">Renewal</option>)}
                                          </select>
                                          {item.company_licence && (
                                            <div className="card mt-2">
                                              <div className="card-body">
                                                <div className="row align-items-baseline form-group mb-2">
                                                  <div className="col-md-6">Subscription Status</div>
                                                  <div className="col-md-6">{item.company_licence.licence_status}</div>
                                                </div>
                                                <div className="row align-items-baseline form-group mb-2">
                                                  <div className="col-md-6">Active Date</div>
                                                  <div className="col-md-6">{format(new Date(item.company_licence.licence_start_period), 'dd MMMM yyyy')}</div>
                                                </div>
                                                <div className="row align-items-baseline form-group">
                                                  <div className="col-md-6">Expired Date</div>
                                                  <div className="col-md-6">{format(new Date(item.company_licence.licence_end_period), 'dd MMMM yyyy')}</div>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <div className="row align-items-baseline form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Is it upgrade?</label>
                                        </div>
                                        <div className="col-md-8">
                                          <div className="form-check form-switch">
                                            <input className="form-check-input" type="checkbox" id={"is-upgrade-" + i} onChange={() => changeQuotationItemData('is_upgrade', !item.is_upgrade, i)}/>
                                            <label className="form-check-label" htmlFor={"is-upgrade-" + i}>{item.is_upgrade ? 'Yes' : 'No'}</label>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="row align-items-center form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Contract Period/Multipliers <span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.contract_long_period)} onChange={(e) => changeQuotationItemData('contract_long_period', numberFormat(e.target.value), i)} disabled={item.service_type === 'Additional'} required/>
                                            <span className="input-group-text mw-85">Month(s)</span>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="row align-items-baseline form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Quantity <span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.product_qty)} onChange={(e) => changeQuotationItemData('product_qty', numberFormat(e.target.value), i)} required/>
                                            <span className="input-group-text mw-85">Unit(s)</span>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="row align-items-baseline form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Tagging <span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <select className="form-select" value={item.product_tagging} onChange={(e) => changeQuotationItemData('product_tagging', e.target.value, i)} required>
                                            <option value=""></option>
                                            {productTaggingOptions.map((item, index) => (
                                              <option key={index} value={item.value}>{item.label}</option>
                                            ))}
                                          </select>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="col-md-6">
                              {item.product_id && (
                                <div>
                                  <div className="card mb-2">
                                    <div className="card-body">
                                      {item.product_currency_original === item.product_currency ? (
                                        <div className="row align-items-baseline form-group mb-2">
                                          <div className="col-md-4">
                                            <label>Cost Price<span className="text-danger">*</span></label>
                                          </div>
                                          <div className="col-md-8">
                                            <div className="input-group">
                                              <input className="form-control text-end" type="text" value={priceFormat(item.product_cost)} onChange={(e) => changeQuotationItemData('product_cost', numberFormat(e.target.value), i)} required/>
                                              <span className="input-group-text">{item.product_currency}</span>
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="row align-items-baseline form-group mb-2">
                                          <div className="col-md-4">
                                            <label>Cost Price<span className="text-danger">*</span></label>
                                          </div>
                                          <div className="col-md-3">
                                            <div className="input-group">
                                              <input className="form-control text-end" type="text" value={item.product_cost_original} onChange={(e) => changeQuotationItemData('product_cost_original', e.target.value, i)} required/>
                                              <span className="input-group-text">{item.product_currency_original}</span>
                                            </div>
                                          </div>
                                          <div className="col-md-5">
                                            <div className="input-group">
                                              <input className="form-control text-end" type="text" value={priceFormat(item.product_cost)} onChange={(e) => changeQuotationItemData('product_cost', numberFormat(e.target.value), i)} required disabled/>
                                              <span className="input-group-text">{quotationCurrency}</span>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      <div className="row align-items-baseline form-group">
                                        <div className="col-md-4">
                                          <label>Subtotal of Cost Price<span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(parseFloat((item.product_cost * item.product_qty * item.contract_long_period).toFixed(3)))} required disabled/>
                                            <span className="input-group-text">{quotationCurrency}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="card mb-2">
                                    <div className="card-body">
                                      {item.product_currency_original === item.product_currency ? (
                                        <div className="row align-items-baseline form-group mb-2">
                                          <div className="col-md-4">
                                            <label>Selling Price<span className="text-danger">*</span></label>
                                          </div>
                                          <div className="col-md-8">
                                            <div className="input-group">
                                              <input className="form-control text-end" type="text" value={priceFormat(item.product_price)} onChange={(e) => changeQuotationItemData('product_price', numberFormat(e.target.value), i)} required/>
                                              <span className="input-group-text">{item.product_currency}</span>
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="row align-items-baseline form-group mb-2">
                                          <div className="col-md-4">
                                            <label>Selling Price<span className="text-danger">*</span></label>
                                          </div>
                                          <div className="col-md-3">
                                            <div className="input-group">
                                              <input className="form-control text-end" type="text" value={item.product_price_original} onChange={(e) => changeQuotationItemData('product_price_original', e.target.value, i)} required/>
                                              <span className="input-group-text">{item.product_currency_original}</span>
                                            </div>
                                          </div>
                                          <div className="col-md-5">
                                            <div className="input-group">
                                              <input className="form-control text-end" type="text" value={priceFormat(item.product_price)} onChange={(e) => changeQuotationItemData('product_price', numberFormat(e.target.value), i)} required disabled/>
                                              <span className="input-group-text">{quotationCurrency}</span>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      <div className="row align-items-baseline form-group">
                                        <div className="col-md-4">
                                          <label>Subtotal of this SKU<span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.product_subtotal)} required disabled/>
                                            <span className="input-group-text">{quotationCurrency}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="card mb-2">
                                    <div className="card-body">
                                      <div className="row align-items-center form-group mb-2">
                                        <div className="col-md-4">
                                          <label>Lumpsum Discount on this SKU</label>
                                        </div>
                                        <div className="col-md-3">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.product_discount_rate, '', true)} onChange={(e) => changeQuotationItemData('product_discount_rate', numberFormat(e.target.value), i)}/>
                                            <span className="input-group-text">%</span>
                                          </div>
                                        </div>
                                        <div className="col-md-5">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.product_discount_price)} onChange={(e) => changeQuotationItemData('product_discount_price', numberFormat(e.target.value), i)}/>
                                            <span className="input-group-text">{quotationCurrency}</span>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="row align-items-baseline form-group">
                                        <div className="col-md-4">
                                          <label>Total of this SKU<span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-8">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.product_total)} required disabled/>
                                            <span className="input-group-text">{quotationCurrency}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="card">
                                    <div className="card-body">
                                      <div className="row align-items-baseline form-group">
                                        <div className="col-md-4">
                                          <label>Margin Amount<span className="text-danger">*</span></label>
                                        </div>
                                        <div className="col-md-3">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.product_margin_rate)} required disabled/>
                                            <span className="input-group-text">%</span>
                                          </div>
                                        </div>
                                        <div className="col-md-5">
                                          <div className="input-group">
                                            <input className="form-control text-end" type="text" value={priceFormat(item.product_margin_price)} required disabled/>
                                            <span className="input-group-text">{quotationCurrency}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {quotationBundleList.map((bundle, i) =>
                      <div className="card mb-3" key={"bundle-" + i}>
                        <div className="card-body">
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="fs-18"><strong>Bundle #{i + 1}</strong></div>
                            <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => removeQuotationBundle(i)}><i className="bi bi-trash me-2"/> Remove</button>
                          </div>
                          <div className="row">
                            <div className="col-md-6">
                              <div className="row align-items-baseline form-group mb-2">
                                <div className="col-md-4">
                                  <label>Bundle Name<span className="text-danger">*</span></label>
                                </div>
                                <div className="col-md-8">
                                  <input className="form-control" type="text" value={bundle.bundle_display_name} onChange={(e) => changeQuotationBundleData('bundle_display_name', e.target.value, i)} required/>
                                </div>
                              </div>
                              <div className="row align-items-baseline form-group mb-2">
                                <div className="col-md-4">
                                  <label>Bundle Description <span className="text-danger">*</span></label>
                                </div>
                                <div className="col-md-8">
                                  <textarea className="form-control" rows="4" value={bundle.bundle_display_description} onChange={(e) => changeQuotationBundleData('bundle_display_description', e.target.value, i)} required/>
                                </div>
                              </div>
                              <div className="row align-items-baseline form-group mb-2">
                                <div className="col-md-4">
                                  <label>SKUs <span className="text-danger">*</span></label>
                                </div>
                                <div className="col-md-8">
                                  <Select value={bundle.bundle_items} options={quotationItemList && quotationItemList.map(product => ({ value: product.product_id, label: product.product_display_name, total: product.product_total }))} onChange={(value) => changeQuotationBundleItem(value, i)} isDisabled={quotationItemList.length === 0} isMulti required />
                                </div>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="row align-items-baseline form-group">
                                <div className="col-md-4">
                                  <label>Subtotal<span className="text-danger">*</span></label>
                                </div>
                                <div className="col-md-8">
                                  <div className="input-group">
                                    <input className="form-control text-end" type="text" value={priceFormat(bundle.bundle_subtotal)} required disabled/>
                                    <span className="input-group-text">{quotationCurrency}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="d-flex">
                      <button className="btn btn-sm btn-outline-secondary" type="button" onClick={addQuotationItem}><i className="bi bi-plus-lg me-2"/> SKU</button>
                      {quotationItemList && quotationItemList.some(item => item.product_id !== '') && (
                        <button className="btn btn-sm btn-outline-secondary ms-2" type="button" onClick={addQuotationBundle}><i className="bi bi-plus-lg me-2"/> Bundle</button>
                      )}
                    </div>
                  </div>
                )}
                
                {quotationItemList && quotationItemList.some(item => item.product_id !== '') && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">Summary</div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-4">
                            <label>Subtotal of all Items<span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-8">
                            <div className="input-group">
                              <input className="form-control text-end" type="text" value={priceFormat(quotationSubtotal)} required disabled/>
                              <span className="input-group-text">{quotationCurrency}</span>
                            </div>
                          </div>
                        </div>
                        <div className="row align-items-center form-group mb-2">
                          <div className="col-md-4">
                            <label>Lumpsum Discount for this QT</label>
                          </div>
                          <div className="col-md-3">
                            <div className="input-group">
                              <input className="form-control text-end" type="text" value={priceFormat(quotationDiscountRate, '', true)} onChange={(e) => changeQuotationDiscount('quotation_discount_rate', numberFormat(e.target.value))}/>
                              <span className="input-group-text">%</span>
                            </div>
                          </div>
                          <div className="col-md-5">
                            <div className="input-group">
                              <input className="form-control text-end" type="text" value={priceFormat(quotationDiscountPrice)} onChange={(e) => changeQuotationDiscount('quotation_discount_price', numberFormat(e.target.value))}/>
                              <span className="input-group-text">{quotationCurrency}</span>
                            </div>
                          </div>
                        </div>
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-4">
                            <label>Total Before Tax<span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-8">
                            <div className="input-group">
                              <input className="form-control text-end" type="text" value={priceFormat(quotationTotal)} required disabled/>
                              <span className="input-group-text">{quotationCurrency}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="row align-items-center form-group mb-2">
                          <div className="col-md-4">
                            <label>Total Cost Price for this QT<span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-8">
                            <div className="input-group">
                              <input className="form-control text-end" type="text" value={priceFormat(quotationCostTotal)} required disabled/>
                              <span className="input-group-text">{quotationCurrency}</span>
                            </div>
                          </div>
                        </div>
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-4">
                            <label>Total Gross Margin<span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-3">
                            <div className="input-group">
                              <input className="form-control text-end" type="text" value={priceFormat(quotationMarginRate)} required disabled/>
                              <span className="input-group-text">%</span>
                            </div>
                          </div>
                          <div className="col-md-5">
                            <div className="input-group">
                              <input className="form-control text-end" type="text" value={priceFormat(quotationMarginTotal)} required disabled/>
                              <span className="input-group-text">{quotationCurrency}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {quotationItemList && quotationItemList.some(item => item.product_id !== '') && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">Payment</div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-4">
                            <label>Payment Schedule <span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-8">
                            <select className="form-select" value={quotationBillingPaymentTerm} onChange={(e) => setQuotationBillingPaymentTerm(e.target.value)} required>
                              <option value="Full Payment">Full Payment</option>
                              <option value="Installment">Installment</option>
                            </select>
                          </div>
                        </div>
                        {quotationBillingPaymentTerm === 'Installment' && (
                          <div className="row align-items-baseline form-group mb-2">
                            <div className="col-md-4">
                              <label>Installment Schedule <span className="text-danger">*</span></label>
                            </div>
                            <div className="col-md-8">
                              <select className="form-select" value={quotationBillingPaymentPeriod} onChange={(e) => setQuotationBillingPaymentPeriod(e.target.value)} disabled={quotationItemList.some(item => item.service_type === 'Additional')} required>
                                <option value="Monthly">Monthly</option>
                                <option value="Quarterly">Quarterly</option>
                                <option value="Annually">Annually</option>
                                <option value="Semesterly">Semesterly</option>
                                <option value="Custom">Custom</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                      {paymentList && paymentList.length > 0 && (
                        <div className="col-md-10 offset-md-2">
                          <div className="table-responsive">
                            <table className="table table-bordered mb-2">
                              <thead>
                                <tr>
                                  <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Schedules</th>
                                  <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Invoice Date</th>
                                  <th className="bg-secondary text-light w-200" style={{textAlign: 'center'}}>Percentage</th>
                                  <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Amount</th>
                                  <th className="bg-secondary text-light"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {paymentList.map((payment, p) =>
                                  <tr key={"payment-" + p}>
                                    <td align="center" valign="middle">Schedule {p + 1}</td>
                                    <td><input className={"form-control " + (payment.error_date ? 'border-danger' : '')} type="date" value={payment.start_term_period} onChange={(e) => changePaymentData('start_term_period', e.target.value, p)} required/></td>
                                    <td>
                                      <div className="input-group">
                                        <input className={"form-control text-end " + (payment.error_rate ? 'border-danger' : '')} type="text" value={priceFormat(payment.payment_rate, '', true)} onChange={(e) => changePaymentData('payment_rate', numberFormat(e.target.value), p)} required/>
                                        <span className="input-group-text">%</span>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="input-group">
                                        <input className="form-control text-end" type="text" value={priceFormat(payment.payment_amount_exc_tax)} onChange={(e) => changePaymentData('payment_amount_exc_tax', numberFormat(e.target.value), p)} required/>
                                        <span className="input-group-text">{quotationCurrency}</span>
                                      </div>
                                    </td>
                                    <td align="center">
                                      <button className="btn btn-sm btn-link text-danger" type="button" onClick={() => removePayment(p)} disabled={paymentList.length === 1}><i className="bi bi-trash"/></button>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                          <div className="d-flex justify-content-start">
                            <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => addPayment()}><i className="bi bi-plus-lg me-2"/> Schedule</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              
                {quotationItemList && quotationItemList.some(item => item.product_id !== '') && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">Attachments</div>
                    </div>
                    <div className="row">
                      <div className="col-md-12">
                        <div className="row align-items-baseline form-group mb-4">
                          <div className="col-md-2">
                            <label>Files <span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-10">
                            {/* <input className="form-control" type="file" multiple={true} accept=".pdf, .png, .jpg, .jpeg, .gif, .webp, .svg, .bmp, .avif, .csv, .xls, .xlsx" onChange={(e) => setAttachmentFiles(Array.from(e.target.files))} required/> */}
                            <div className="table-responsive">
                              <table className="table table-bordered mb-2">
                                <thead>
                                  <tr>
                                    <th className="bg-secondary text-light" style={{textAlign: 'center'}}>#</th>
                                    <th className="bg-secondary text-light w-400">Name</th>
                                    <th className="bg-secondary text-light">File</th>
                                    <th className="bg-secondary text-light"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {attachmentList && attachmentList.map((attachment, a) =>
                                    <tr key={"attachment-" + a}>
                                      <td valign="middle" align="center">{a + 1}</td>
                                      <td valign="middle">
                                        <input className="form-control" type="text" value={attachment.name} onChange={(e) => changeAttachmentData('name', e.target.value, a)} required />
                                      </td>
                                      <td valign="middle">
                                        {attachment.loading === false ? (
                                          <div>
                                            {attachment.files ? (
                                              <a className="ellipsis" href={attachment.files[0].fileUrl} target="_blank" rel="noopener noreferrer">{shortenUrl(attachment.files[0].fileUrl, 50)} <i className="bi bi-link-45deg ms-2"/></a>
                                            ) : (
                                              <input className="form-control" type="file" accept=".pdf, .png, .jpg, .jpeg, .gif, .webp, .svg, .bmp, .avif, .csv, .xls, .xlsx" onChange={(e) => uploadAttachments(Array.from(e.target.files), a)} required />
                                            )}
                                          </div>
                                        ) : 'Loading...'}
                                      </td>
                                      <td valign="middle" align="center">
                                        <button className="btn btn-sm btn-link text-danger" type="button" onClick={() => removeAttachments(a)}><i className="bi bi-trash"/></button>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                            <div className="d-flex justify-content-start">
                              <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => addAttachments()}><i className="bi bi-plus-lg me-2"/> Attachment</button>
                            </div>
                          </div>
                        </div>
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-2">
                            <label>Quotation Remarks</label>
                          </div>
                          <div className="col-md-10">
                            <textarea className="form-control" rows="4" value={quotationRemarks} placeholder="Additional information for this Quotation..." onChange={(e) =>setQuotationRemarks(e.target.value)}/>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {quotationItemList && quotationItemList.some(item => item.product_id !== '') && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">Terms &amp; Conditions</div>
                    </div>
                    {quotationItemList.some(item => item.product_type === 'Subscription') && (
                      <div className="row mb-4">
                        <div className="col-md-12">
                          <div className="row align-items-baseline form-group mb-2">
                            <div className="col-md-2">
                              <label>Subscription Terms <span className="text-danger">*</span></label>
                            </div>
                            <div className="col-md-10">
                              <textarea className="form-control mb-2" rows="8" value={quotationLegalTerms.subscription} onChange={(e) => setQuotationLegalTerms(prev => ({ ...prev, subscription: e.target.value }))} required/>
                              <div className="d-flex align-items-center">
                                <Select className="w-400" value={selectedTermTemplate.subscription} options={categoryList} placeholder="Select Terms Template..." onChange={(value) => setSelectedTermTemplate(prev => ({ ...prev, subscription: value }))}/>
                                <button className="btn btn-outline-secondary ms-2" type="button" onClick={() => changeSubscriptionTerm()}>Insert</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="row mb-4">
                      <div className="col-md-12">
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-2">
                            <label>Payment Terms <span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-10">
                            <textarea className="form-control mb-2" rows="8" value={quotationLegalTerms.payment} onChange={(e) => setQuotationLegalTerms(prev => ({ ...prev, payment: e.target.value }))} required/>
                            <div className="d-flex align-items-center">
                              <Select className="w-400" value={selectedTermTemplate.payment} options={paymentTermOptions} placeholder="Select Terms Template..." onChange={(value) => setSelectedTermTemplate(prev => ({ ...prev, payment: value }))}/>
                              <button className="btn btn-outline-secondary ms-2" type="button" onClick={() => changePaymentTerm()}>Insert</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-12">
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-2">
                            <label>Cancellation Terms <span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-10">
                            <textarea className="form-control mb-2" rows="8" value={quotationLegalTerms.cancellation} onChange={(e) => setQuotationLegalTerms(prev => ({ ...prev, cancellation: e.target.value }))} required/>
                            <div className="d-flex align-items-center">
                              <Select className="w-400" value={selectedTermTemplate.cancellation} options={cancellationTermOptions} placeholder="Select Terms Template..." onChange={(value) => setSelectedTermTemplate(prev => ({ ...prev, cancellation: value }))}/>
                              <button className="btn btn-outline-secondary ms-2" type="button" onClick={() => changeCancellationTerm()}>Insert</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="mb-4">
                  <div className="d-flex justify-content-end">
                    <a className="btn btn-link" href="/quotation">Cancel</a>
                    <button className="btn btn-primary ms-2" type="button" onClick={() => submitQuotationAdd()} disabled={isLoadingSubmit}>{isLoadingSubmit ? 'Loading...' : 'Save'}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationAdd;


